import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useUserTokenStore from "@/stores/user-token.store";
import { jwtDecode } from "jwt-decode";
import { logger } from "@/lib/logger";
import {
  getAuth0ErrorCode,
  isRecoveringAuth,
  recoverFromInteractiveAuthError,
  resetAuthRecovery,
} from "@/lib/auth/authRecovery";
import {
  buildSilentAuthOptions,
  getSdkOrganizationId,
  refreshAccessTokenOnce,
} from "@/lib/auth/cachedAccessToken";
import { requireAccessToken } from "@/lib/auth/accessToken";
import {
  MAX_TRANSIENT_REFRESH_RETRIES,
  computeRefreshBackoffMs,
} from "@/lib/auth/refreshBackoff";

type DecodedToken = {
  exp: number; // expiration time in seconds since epoch
  permissions?: string[]; // permissions array, optional
};

/** Poll while the recovery breaker is latched without org context (redirect in flight or awaiting re-arm). */
const RECOVERY_POLL_MS = 2_000;

const TokenRefresher = () => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, user } =
    useAuth0();
  const sdkOrganizationId = getSdkOrganizationId(user);
  const { setToken, setPermissions } = useUserTokenStore();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const transientRetriesRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    // A fresh authenticated cycle starts backoff from scratch so a maxed-out
    // counter from a previous session can't suppress the first retry (LAR-290).
    transientRetriesRef.current = 0;
    // Per-effect latch: an effect rerun (e.g. sdkOrganizationId hydrating) tears
    // this closure down, but an already-awaited silent refresh can still resolve.
    // Bailing on `cancelled` stops that stale call from scheduling or overlapping
    // a new refresh, which would risk refresh-token rotation reuse (LAR-290).
    let cancelled = false;

    const scheduleRefresh = async () => {
      if (cancelled) return;

      const silentAuthOptions = buildSilentAuthOptions(sdkOrganizationId);
      const hasOrgContext = silentAuthOptions !== undefined;

      const recovering = isRecoveringAuth();

      if (recovering) {
        // Layer 2: org may hydrate after a no-org failure tripped the breaker —
        // try org-aware silent refresh instead of skipping forever (LAR-310).
        if (!hasOrgContext) {
          // Redirect may still be in flight; poll so resetAuthRecovery() after a
          // failed redirect can resume proactive refresh.
          timeoutRef.current = setTimeout(scheduleRefresh, RECOVERY_POLL_MS);
          return;
        }
      }

      try {
        // Force a network refresh while latched — the SDK can otherwise return a
        // still-valid cached AT after interactive-required failed, which would
        // falsely clear the breaker without restoring refresh (LAR-310). Off the
        // recovery path, join the shared single-flight refresh so this timer and
        // a waking tab's requests can't rotate the same token twice (LAR-361).
        // The recovery branch calls the SDK directly, so it has to apply the same
        // no-token-is-not-a-token rule `refreshAccessTokenOnce` applies (LAR-437); a miss here
        // is a failed refresh, and the catch below backs off rather than storing a non-token.
        const token = recovering
          ? requireAccessToken(
              await getAccessTokenSilently({
                ...silentAuthOptions,
                cacheMode: "off",
              }),
              "TokenRefresher.recoveryRefresh"
            )
          : await refreshAccessTokenOnce(
              getAccessTokenSilently,
              sdkOrganizationId
            );
        if (cancelled) return;

        // Another consumer may have tripped the breaker while this await was in
        // flight — poll the recovery path instead of sleeping until token expiry.
        if (!recovering && isRecoveringAuth()) {
          timeoutRef.current = setTimeout(scheduleRefresh, RECOVERY_POLL_MS);
          return;
        }

        if (recovering) {
          resetAuthRecovery();
        }
        setToken(token);
        transientRetriesRef.current = 0;

        const { exp, permissions } = jwtDecode<DecodedToken>(token);
        const refreshInMs = exp * 1000 - Date.now() - 60_000; // refresh 1 min before expiry

        setPermissions(permissions || []);
        // Safety fallback
        const delay = Math.max(refreshInMs, 5_000); // at least 10s from now

        timeoutRef.current = setTimeout(scheduleRefresh, delay);
      } catch (err) {
        if (cancelled) return;

        // Interactive-required (rotation breach / dead silent-auth): recover to
        // an interactive login once instead of leaving the app on a stale token
        // and looping (LAR-290).
        if (recoverFromInteractiveAuthError(err, loginWithRedirect)) {
          timeoutRef.current = setTimeout(scheduleRefresh, RECOVERY_POLL_MS);
          return;
        }

        // Transient (network/timeout): bounded exponential backoff so a blip
        // doesn't kill the refresher permanently, but never a tight loop.
        const attempt = transientRetriesRef.current;
        logger.error("Failed to refresh access token", {
          operation: "refreshAccessToken",
          error: err,
          transientRetry: attempt,
          auth0Error: getAuth0ErrorCode(err),
        });
        if (attempt < MAX_TRANSIENT_REFRESH_RETRIES) {
          transientRetriesRef.current = attempt + 1;
          timeoutRef.current = setTimeout(
            scheduleRefresh,
            computeRefreshBackoffMs(attempt)
          );
        }
      }
    };

    scheduleRefresh();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
    sdkOrganizationId,
    setToken,
    setPermissions,
  ]);

  return null;
};

export default TokenRefresher;
